import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { format } from 'date-fns';
import { AlertCircle, Briefcase, CalendarDays, Eye, FileText, Sparkles } from 'lucide-react';

import { useJob } from '@/hooks/useJobs';
import { composeJobBody } from '@/lib/jobBody';
import { formatSalary } from '@/lib/formatSalary';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Markdown } from '@/components/ui/markdown';
import { StatCard } from '@/components/dashboard/StatCard';
import { JOB_TYPE_LABELS, WORK_MODE_LABELS } from '@repo/types';

export const Route = createFileRoute('/admin/jobs/$id/details')({
  component: JobDetailsPage,
});

/** One labelled row in an aside card. */
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  if (children == null || children === '') return null;
  return (
    <div className="flex items-start justify-between gap-3 text-sm">
      <span className="shrink-0 text-muted-foreground">{label}</span>
      <span className="text-right font-medium">{children}</span>
    </div>
  );
}

/** Titled content card used for description/requirements sections. */
function SectionCard({
  icon: Icon,
  title,
  children,
}: {
  icon: React.ElementType;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2.5 text-base">
          <span className="flex size-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Icon className="size-4" />
          </span>
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function JobDetailsPage() {
  const { id } = Route.useParams();
  const { data: job, isLoading, error } = useJob(id);

  if (isLoading) {
    return (
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Skeleton className="h-24 w-full rounded-xl" />
          <Skeleton className="h-64 w-full rounded-xl" />
        </div>
        <Skeleton className="h-80 w-full rounded-xl" />
      </div>
    );
  }

  if (error || !job) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="size-4" />
        <AlertDescription>
          {error instanceof Error ? error.message : 'Failed to load job details. Please try again.'}
        </AlertDescription>
      </Alert>
    );
  }

  const experience =
    job.experienceMin != null && job.experienceMax != null
      ? `${job.experienceMin}–${job.experienceMax} yrs`
      : job.experienceMin != null
        ? `${job.experienceMin}+ yrs`
        : job.experienceMax != null
          ? `Up to ${job.experienceMax} yrs`
          : 'Not specified';

  const salary = formatSalary(job.salaryMin, job.salaryMax, job.salaryCurrency);
  const benefits = job.benefits ? Object.entries(job.benefits) : [];

  // Show the portal's own posting date when we have it; otherwise fall back to
  // our arrival time and SAY that's what it is, so the tile never claims a
  // posting date we don't actually know.
  const dateStat = job.postedDate
    ? { label: 'Posted', value: job.postedDate }
    : { label: job.source ? 'Scraped' : 'Added', value: job.createdAt };

  // The posting reads as one document, so it renders as one card.
  const body = composeJobBody(job);

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      {/* Main column */}
      <div className="space-y-6 lg:col-span-2">
        <div className="grid gap-4 sm:grid-cols-3">
          <StatCard label="Views" value={(job.viewCount ?? 0).toLocaleString()} icon={Eye} />
          <StatCard
            label={dateStat.label}
            value={format(new Date(dateStat.value), 'MMM d, yyyy')}
            icon={CalendarDays}
          />
          <StatCard label="Experience" value={experience} icon={Briefcase} />
        </div>

        {body && (
          <SectionCard icon={FileText} title="Job Description">
            <Markdown>{body}</Markdown>
          </SectionCard>
        )}
      </div>

      {/* Aside */}
      <aside className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Overview</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2.5">
            <Field label="Job Type">
              {job.jobType ? (JOB_TYPE_LABELS[job.jobType] ?? job.jobType) : '—'}
            </Field>
            <Field label="Work Mode">
              {job.workMode ? (WORK_MODE_LABELS[job.workMode] ?? job.workMode) : '—'}
            </Field>
            <Field label="Category">{job.category || '—'}</Field>
            <Field label="Industry">{job.industry || '—'}</Field>
            <Field label="Experience">{experience}</Field>
            {salary && (
              <>
                <Separator className="my-1" />
                <Field label="Salary">{salary}</Field>
              </>
            )}
            {job.suggestedCategory && (
              <>
                <Separator className="my-1" />
                <Field label="Suggested">
                  <span className="inline-flex items-center gap-1.5">
                    {job.suggestedCategory}
                    {job.categoryConfidence != null && (
                      <Badge variant="outline" className="text-[10px]">
                        {Math.round(job.categoryConfidence * 100)}%
                      </Badge>
                    )}
                  </span>
                </Field>
              </>
            )}
          </CardContent>
        </Card>

        {job.skills && job.skills.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Sparkles className="size-4 text-primary" />
                Skills
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-1.5">
                {job.skills.map((skill) => (
                  <Badge key={skill} variant="secondary" className="font-normal">
                    {skill}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {benefits.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Benefits</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm text-foreground/90">
                {benefits.map(([key, value]) => (
                  <li key={key} className="flex gap-2.5">
                    <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-primary" />
                    {String(value)}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Timeline</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2.5">
            {/* Each row says what actually happened rather than naming a column:
                "Job posted" is the portal's own date, "Scraped on"/"Added on" is
                when the row reached us. `updated_at` is deliberately absent — it
                moves on any write (a backfill, a status change) and means nothing
                to someone reviewing the posting. */}
            <Field label="Job posted">
              {job.postedDate ? format(new Date(job.postedDate), 'MMM d, yyyy') : 'Not stated'}
            </Field>
            <Field label={job.source ? 'Scraped on' : 'Added on'}>
              {format(new Date(job.createdAt), 'MMM d, yyyy')}
            </Field>
            {job.expiryDate && (
              <Field label="Expires">{format(new Date(job.expiryDate), 'MMM d, yyyy')}</Field>
            )}
            {job.expiredAt && (
              <Field label="Expired">{format(new Date(job.expiredAt), 'MMM d, yyyy')}</Field>
            )}
          </CardContent>
        </Card>
      </aside>
    </div>
  );
}
